import { BasicInput } from '@dev/ee-components';
import { clone } from 'lodash-es';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ErrorSubscriberOrder } from '@/components/CampaignCreator/CampaignCreatorInterface';
import { hasEmailUnallowedChars } from '@/components/CampaignCreator/helpers';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import { useGetCampaignInfo } from '@/components/CampaignCreator/hooks/useGetCampaignInfo';
import { INDEX_PLACEHOLDER } from '@/services/constants';
import {
    getCampainCreatorStore,
    ModifyCampaignMap,
    SetCampaignErrors,
    SetCampaignErrorsSubscriber,
} from '@/store/campaignCreator/actions';
import { CampaignErrorNames } from '@/store/campaignCreator/types';
import { EmailDropdown, ReplyToCheckbox } from '../../components';
import { CampaignFromProps } from '../../SubjectAndContentInterface';
import { BasicInputWrapper, EmailDropdownWrapper, Wrapper } from '../styledComponents';

export const CampaignFrom = (props: CampaignFromProps): JSX.Element => {
    const { showReplyTo, setShowReplyTo } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const { activeTemplateIndex, errors, isFromEmailFree } = useSelector(getCampainCreatorStore);
    const { fromName, fromEmail } = useGetCampaignInfo();

    const emailDropdownId = `campaign-creator-emaildropdown-emailfrom-${activeTemplateIndex}`;
    const emailDropdownEmptyErrorName = CampaignErrorNames.FROM_EMAIL.replace(
        INDEX_PLACEHOLDER,
        `${activeTemplateIndex}`
    );
    const emailDropdownEmptyHasError = !!errors?.[emailDropdownEmptyErrorName];
    const emailDropdownNotAssociatedErrorName = CampaignErrorNames.FROM_EMAIL_NOT_ASSOCIATED.replace(
        INDEX_PLACEHOLDER,
        `${activeTemplateIndex}`
    );
    const emailDropdownNotAssociatedHasError = !!errors?.[emailDropdownNotAssociatedErrorName];
    const emailDropdownHasError = !!emailDropdownEmptyHasError || !!emailDropdownNotAssociatedHasError;

    const errorMessage = emailDropdownEmptyHasError ? t('thisfieldisrequired') : t('emailnotassociatederror');

    useEffect((): void => {
        dispatch(
            SetCampaignErrorsSubscriber({
                id: emailDropdownId,
                errorName: emailDropdownEmptyErrorName,
                order: ErrorSubscriberOrder.TEMPLATE_SUBJECT,
            })
        );
    }, [activeTemplateIndex]);

    useEffect((): void => {
        dispatch(
            SetCampaignErrorsSubscriber({
                id: emailDropdownId,
                errorName: emailDropdownNotAssociatedErrorName,
                order: ErrorSubscriberOrder.TEMPLATE_SUBJECT,
            })
        );
    }, [activeTemplateIndex]);

    const setFromName = (value: string): void => {
        const modifyMap = new Map<string, any>();
        modifyMap.set(`CampaignTemplates[${activeTemplateIndex}].Sender.FromName`, value);
        !showReplyTo && modifyMap.set(`CampaignTemplates[${activeTemplateIndex}].Sender.ReplyName`, value);
        dispatch(ModifyCampaignMap(modifyMap));
    };

    const cancelErrors = () => {
        const errorsObject = clone(errors) || {};
        errorsObject[CampaignErrorNames.FROM_EMAIL.replace(INDEX_PLACEHOLDER, `${activeTemplateIndex}`)] = false;
        errorsObject[
            CampaignErrorNames.FROM_EMAIL_NOT_ASSOCIATED.replace(INDEX_PLACEHOLDER, `${activeTemplateIndex}`)
        ] = false;
        dispatch(SetCampaignErrors(errorsObject));
    };

    const setFromEmail = (value: string): void => {
        if (hasEmailUnallowedChars(value)) {
            return;
        }
        const modifyMap = new Map<string, any>();
        modifyMap.set(`CampaignTemplates[${activeTemplateIndex}].Sender.From`, [value]);
        !showReplyTo && modifyMap.set(`CampaignTemplates[${activeTemplateIndex}].Sender.ReplyTo`, [value]);
        dispatch(ModifyCampaignMap(modifyMap));
        cancelErrors();
    };

    const changeFromNameHandle = (ev) => {
        setFromName(ev.currentTarget.value);
    };

    return (
        <Wrapper>
            <BasicInputWrapper>
                <BasicInput
                    id="input-from-name"
                    label={t('fromname')}
                    value={fromName}
                    onChange={changeFromNameHandle}
                    readOnly={isDetailsScreen}
                />
                <ReplyToCheckbox showReplyTo={showReplyTo} setShowReplyTo={setShowReplyTo} />
            </BasicInputWrapper>
            <EmailDropdownWrapper>
                <EmailDropdown
                    setFromEmail={setFromEmail}
                    fromEmail={fromEmail}
                    hasError={emailDropdownHasError}
                    errorMessage={errorMessage}
                    id={emailDropdownId}
                    isTipVisible={isFromEmailFree}
                    isFromEmail
                    label={t('frommail')}
                />
            </EmailDropdownWrapper>
        </Wrapper>
    );
};
